/**
 * Help Center FAQ Data Loader (Enhanced Version)
 * Fully dynamic navigation and content generation
 */

class HelpCenterFAQLoader {
  constructor() {
    this.faqData = {};
    this.tutorialData = {};
    this.currentCategory = 'phone-cases';
    this.config = window.helpCenterConfig || {};
    this.descClass = this.config.descClass || 'text-size-12 sm:text-size-14 lg:text-size-16 font-light';
    this.titleClass = this.config.titleClass || 'text-size-24 leading-none';

    // Use shared category structure from common utility
    this.categoryStructure = window.HelpCenterCommon
      ? window.HelpCenterCommon.categoryStructure
      : this.getFallbackCategoryStructure();
  }

  /**
   * Fallback category structure if common utility not loaded
   */
  getFallbackCategoryStructure() {
    return {
      'phone-cases': {
        label: 'Phone Cases',
        hasSubmenu: true,
        subsections: [
          { id: 'phone-cases-general', label: 'General Questions' },
          { id: 'phone-cases-edge', label: 'Edge Case' },
          { id: 'phone-cases-summa', label: 'Summa Case' },
          { id: 'phone-cases-cairn', label: 'Cairn Case' },
          { id: 'phone-cases-aaron', label: 'Aaron Case' }
        ]
      },
      'phone-gears': {
        label: 'Phone Gears',
        hasSubmenu: true,
        subsections: [
          { id: 'phone-gears-wallets', label: 'Card Wallets' },
          { id: 'phone-gears-powerbanks', label: 'Power Banks' },
          { id: 'phone-gears-carmounts', label: 'Car Mounts' },
          { id: 'phone-gears-grip', label: 'MagEZ Grip' },
          { id: 'phone-gears-straps', label: 'Phone Straps' }
        ]
      },
      'tablet-cases': {
        label: 'Tablet Cases',
        hasSubmenu: true,
        subsections: [
          { id: 'tablet-air-case', label: 'Air Case' },
          { id: 'tablet-aramid-woven', label: 'MagEZ Case 2' },
          { id: 'tablet-charging-cases', label: 'Aramid Woven Charging Cases' },
          { id: 'tablet-folio2', label: 'Folio 2' },
          { id: 'tablet-ipad-stand', label: 'iPad Stand' }
        ]
      },
      'watch-airpods': {
        label: 'Watch&Airpods Gears',
        hasSubmenu: true,
        subsections: [
          { id: 'watch-bands', label: 'Carbon Fiber Watch Bands' },
          { id: 'watch-aramid-cases', label: 'Aramid Woven Cases' },
          { id: 'airpods-cases', label: 'AirPods Cases' }
        ]
      },
      // 'shipping': { label: 'Shipping', hasSubmenu: false },
      // 'order': { label: 'Order', hasSubmenu: false },
      // 'refund-return': { label: 'Refund & Return', hasSubmenu: false }
    };
  }

  /**
   * Load JSON data from file
   */
  async loadJSON(category, type) {
    try {
      // Get URL from config (generated by Liquid)
      const url = this.config.assetUrls?.[type]?.[category];
      if (!url) {
        return { title: '', sections: [] };
      }

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return { title: '', sections: [] };
    }
  }

  /**
   * Generate navigation HTML
   */
  generateNavigation() {
    const navList = document.getElementById('nav-category-list');
    const mobileTabs = document.getElementById('mobile-category-tabs');

    if (!navList) {
      return;
    }

    // Generate desktop navigation (with submenus)
    const desktopHtml = Object.entries(this.categoryStructure).map(([catId, catData]) => {
      if (catData.hasSubmenu) {
        return `
          <li class="nav-category-item">
            <button class="nav-category-btn ${this.titleClass} w-full flex items-center justify-between py-2 hover:font-medium transition-all text-left" data-category="${catId}">
              <span>${catData.label}</span>
              <svg class="w-4 h-4 transform transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>
            <ul class="nav-submenu hidden pl-4 space-y-2 mt-2">
              ${catData.subsections.map(sub => `
                <li><a href="#${sub.id}" class="subcategory-link ${this.descClass} text-grey block py-1.5 hover:text-black transition-all">${sub.label}</a></li>
              `).join('')}
            </ul>
          </li>
        `;
      } else {
        return `
          <li class="nav-category-item">
            <a href="#${catId}" class="nav-category-link ${this.titleClass} block py-2 hover:font-medium transition-all" data-category="${catId}">${catData.label}</a>
          </li>
        `;
      }
    }).join('');

    navList.innerHTML = desktopHtml;

    // Generate mobile tabs (horizontal, no submenus)
    if (mobileTabs) {
      const categories = Object.entries(this.categoryStructure);
      const mobileHtml = `
        <div class="flex items-center whitespace-nowrap">
          ${categories.map(([catId, catData], index) => `
            <button class="mobile-category-tab ${this.descClass} transition-all ${catId === this.currentCategory ? 'active text-black font-medium' : 'text-grey'}" data-category="${catId}">
              ${catData.label}
            </button>
            ${index < categories.length - 1 ? '<span class="mx-4 text-grey">|</span>' : ''}
          `).join('')}
        </div>
      `;
      mobileTabs.innerHTML = mobileHtml;
    }

    this.attachNavListeners();

    // Reattach tutorials.js listeners after regenerating navigation
    if (typeof window.tutorialsReattachListeners === 'function') {
      window.tutorialsReattachListeners();
    }
  }

  /**
   * Scroll tab into view with boundary check (uses shared utility)
   */
  scrollTabIntoView(tab) {
    if (window.HelpCenterCommon) {
      window.HelpCenterCommon.scrollTabIntoView(tab, '[HelpCenter]');
    }
  }

  /**
   * Attach navigation event listeners
   */
  attachNavListeners() {
    // Mobile category tabs
    const mobileTabs = document.querySelectorAll('.mobile-category-tab');

    mobileTabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        const category = tab.dataset.category;

        // Check if on Tutorials tab
        const tutorialsTab = document.getElementById('tutorials-tab');
        const onTutorialsTab = tutorialsTab && tutorialsTab.classList.contains('active');

        if (category && !onTutorialsTab) {
          // Clear search input when navigating
          this.clearSearchInput();
          this.loadCategoryContent(category);

          // Update active state using shared utility
          if (window.HelpCenterCommon) {
            window.HelpCenterCommon.updateMobileTabState(mobileTabs, tab);
          }

          // Scroll the tab into view (only if not fully visible)
          this.scrollTabIntoView(tab);
        }
      });
    });

    // Category toggle buttons
    const categoryBtns = document.querySelectorAll('.nav-category-btn');

    categoryBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();

        const submenu = btn.nextElementSibling;
        const svg = btn.querySelector('svg');
        const isCurrentlyOpen = submenu && !submenu.classList.contains('hidden');

        // Close all other submenus first (accordion behavior)
        categoryBtns.forEach(otherBtn => {
          if (otherBtn !== btn) {
            const otherSubmenu = otherBtn.nextElementSibling;
            const otherSvg = otherBtn.querySelector('svg');
            if (otherSubmenu && otherSubmenu.classList.contains('nav-submenu')) {
              otherSubmenu.classList.add('hidden');
              if (otherSvg) {
                otherSvg.style.transform = 'rotate(0deg)';
              }
            }
          }
        });

        // Toggle current submenu
        if (submenu && submenu.classList.contains('nav-submenu')) {
          if (isCurrentlyOpen) {
            submenu.classList.add('hidden');
            if (svg) svg.style.transform = 'rotate(0deg)';
          } else {
            submenu.classList.remove('hidden');
            if (svg) svg.style.transform = 'rotate(180deg)';
          }
        }

        // Only load FAQ content if on FAQs tab
        const tutorialsTab = document.getElementById('tutorials-tab');
        const onTutorialsTab = tutorialsTab && tutorialsTab.classList.contains('active');

        const category = btn.dataset.category;
        if (category && !onTutorialsTab) {
          // Clear search input when navigating
          this.clearSearchInput();
          this.loadCategoryContent(category);
        }
      });
    });

    // Category links (for non-submenu categories)
    const categoryLinks = document.querySelectorAll('.nav-category-link');

    categoryLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const category = link.dataset.category;

        // Only load FAQ content if on FAQs tab
        const tutorialsTab = document.getElementById('tutorials-tab');
        const onTutorialsTab = tutorialsTab && tutorialsTab.classList.contains('active');

        if (category && !onTutorialsTab) {
          // Clear search input when navigating
          this.clearSearchInput();
          this.loadCategoryContent(category);
        }
      });
    });

    // Subcategory links (scroll to section)
    const subcategoryLinks = document.querySelectorAll('.subcategory-link');

    subcategoryLinks.forEach(link => {
      link.addEventListener('click', async (e) => {
        // Check if we're on the Tutorials tab - if so, let tutorials.js handle it
        const tutorialsTab = document.getElementById('tutorials-tab');
        if (tutorialsTab && tutorialsTab.classList.contains('active')) {
          return; // Don't prevent default, let tutorials.js handle it
        }

        e.preventDefault();
        const targetId = link.getAttribute('href');

        if (targetId) {
          // Extract category from target ID (e.g., "phone-cases-general" -> "phone-cases")
          const parts = targetId.substring(1).split('-');
          let category = '';

          // Try to match with known categories
          for (const [catId, catData] of Object.entries(this.categoryStructure)) {
            if (targetId.includes(catId)) {
              category = catId;
              break;
            }
          }

          // Load category content first (if not already loaded or different category)
          if (category && category !== this.currentCategory) {
            // Clear search input when navigating to different category
            this.clearSearchInput();
            await this.loadCategoryContent(category, false); // Don't scroll to top, we'll scroll to subsection

            // Wait a bit for DOM to update
            await new Promise(resolve => setTimeout(resolve, 100));
          }

          // Wait a bit more to ensure DOM is fully updated
          await new Promise(resolve => setTimeout(resolve, 150));

          // Scroll the page to the target section
          const targetSection = document.querySelector(targetId);

          if (targetSection) {
            // Calculate offset to avoid showing header
            // Increased offset to ensure header is never visible
            const offset = 200; // Distance from top of viewport (header + tabs + search + padding)
            const minScrollPosition = 250; // Minimum scroll position to ensure header stays hidden
            let targetPosition = targetSection.getBoundingClientRect().top + window.pageYOffset - offset;

            // Ensure we never scroll to a position that would show the header
            targetPosition = Math.max(minScrollPosition, targetPosition);

            // Smooth scroll to target position
            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });

            // Update active state
            subcategoryLinks.forEach(l => l.classList.remove('active', 'font-medium', 'text-black'));
            link.classList.add('active', 'font-medium', 'text-black');
          }
        }
      });
    });
  }

  /**
   * Generate FAQ content HTML for a category
   */
  async generateFAQContent(category) {
    const catData = this.categoryStructure[category];
    if (!catData) return '';

    // Load FAQ data
    if (!this.faqData[category]) {
      this.faqData[category] = await this.loadJSON(category, 'faq');
    }

    const data = this.faqData[category];
    let html = `<div class="faq-section mb-12" id="${category}" data-category="${category}">`;
    html += `<h3 class="${this.titleClass} mb-6 pb-3 border-b">${catData.label}</h3>`;

    if (catData.hasSubmenu && catData.subsections) {
      // Generate subsections
      catData.subsections.forEach(subsection => {
        const sectionData = data.sections.find(s => s.id === subsection.id);
        html += `
          <div class="faq-subsection mb-8" id="${subsection.id}" data-section-id="${subsection.id}">
            <h4 class="text-size-14 sm:text-size-18 lg:text-size-20 font-medium mb-4">${subsection.label}</h4>
            <div class="faq-items">
              ${sectionData ? this.renderFAQItemsHTML(sectionData.items) : '<p class="text-grey italic">No FAQs available</p>'}
            </div>
          </div>
        `;
      });
    } else {
      // Single section category
      const sectionData = data.sections[0];
      html += `
        <div class="faq-items">
          ${sectionData && sectionData.items ? this.renderFAQItemsHTML(sectionData.items) : '<p class="text-grey italic">No FAQs available</p>'}
        </div>
      `;
    }

    html += '</div>';
    return html;
  }

  /**
   * Render FAQ items as HTML string
   */
  renderFAQItemsHTML(items) {
    if (!items || items.length === 0) return '';

    return items.map(item => `
      <div class="faq-item border-b">
        <button class="faq-question ${this.descClass} w-full text-left py-4 flex items-start justify-between gap-4">
          <span class="flex-1">${this.escapeHtml(item.question)}</span>
          <svg class="w-4 h-4 flex-shrink-0 transform transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </button>
        <div class="faq-answer ${this.descClass} text-grey font-light hidden pb-4 pl-0">
          ${this.parseAnswer(item.answer)}
        </div>
      </div>
    `).join('');
  }

  /**
   * Parse answer text with markdown support
   */
  parseAnswer(answer) {
    if (!answer) return '';
    const paragraphs = answer.split('\n\n');
    return paragraphs.map(para => {
      if (para.trim() === '') return '';
      para = para.replace(/\*\*(.*?)\*\*/g, '<span class="font-medium">$1</span>');
      para = para.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" class="text-black underline">$1</a>');
      para = para.replace(/\n/g, '<br>');
      if (!para.trim().startsWith('<')) para = `<p class="mb-2">${para}</p>`;
      return para;
    }).join('');
  }

  /**
   * Escape HTML
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Load and display category content
   */
  async loadCategoryContent(category, scrollToTop = true) {
    this.currentCategory = category;
    const container = document.getElementById('faq-content-container');
    if (!container) return;

    // Show loading
    container.innerHTML = '<div class="faq-loading text-center py-12 text-grey italic">Loading FAQs...</div>';

    // Generate and display content
    const html = await this.generateFAQContent(category);
    container.innerHTML = html;

    // Attach FAQ item listeners
    this.attachFAQListeners(container);

    // Update mobile tab active state
    const mobileTabs = document.querySelectorAll('.mobile-category-tab');
    let activeTab = null;
    mobileTabs.forEach(tab => {
      if (tab.dataset.category === category) {
        tab.classList.add('active', 'text-black', 'font-medium');
        tab.classList.remove('text-grey');
        activeTab = tab;
      } else {
        tab.classList.remove('active', 'text-black', 'font-medium');
        tab.classList.add('text-grey');
      }
    });

    // Scroll active tab into view
    if (activeTab) {
      this.scrollTabIntoView(activeTab);
    }

    // Do not auto-scroll when loading category
    // User will scroll manually or use subcategory links for page-level scrolling
  }

  /**
   * Attach FAQ toggle listeners
   */
  attachFAQListeners(container) {
    const faqButtons = container.querySelectorAll('.faq-question');
    faqButtons.forEach(button => {
      button.addEventListener('click', () => {
        const item = button.parentElement;
        const answer = button.nextElementSibling;
        const svg = button.querySelector('svg');
        if (answer.classList.contains('hidden')) {
          answer.classList.remove('hidden');
          item.classList.add('active');
          if (svg) svg.style.transform = 'rotate(180deg)';
        } else {
          answer.classList.add('hidden');
          item.classList.remove('active');
          if (svg) svg.style.transform = 'rotate(0deg)';
        }
      });
    });
  }

  /**
   * Initialize search functionality
   */
  initSearch() {
    const searchInput = document.getElementById('faqSearch');
    const searchResults = document.getElementById('searchResults');

    if (!searchInput || !searchResults) {
      return;
    }

    let searchTimeout;

    searchInput.addEventListener('input', (e) => {
      const query = e.target.value.trim();

      // Clear previous timeout
      clearTimeout(searchTimeout);

      // Debounce search
      searchTimeout = setTimeout(() => {
        if (query.length < 1) {
          searchResults.textContent = '';

          // Check which tab is active
          const tutorialsTab = document.getElementById('tutorials-tab');
          const isTutorialsActive = tutorialsTab && tutorialsTab.classList.contains('active');

          if (isTutorialsActive) {
            // Clear tutorial search if exists
            if (window.clearTutorialSearch) {
              window.clearTutorialSearch();
            }
          } else {
            this.clearSearchFilter();
          }
          return;
        }

        // Check which tab is active and perform appropriate search
        const tutorialsTab = document.getElementById('tutorials-tab');
        const isTutorialsActive = tutorialsTab && tutorialsTab.classList.contains('active');

        if (isTutorialsActive) {
          // Perform tutorial search
          if (window.performTutorialSearch) {
            window.performTutorialSearch(query);
          }
        } else {
          // Perform FAQ search
          this.performSearch(query);
        }
      }, 300);
    });
  }

  /**
   * Perform search and filter FAQ items
   */
  async performSearch(query) {
    const searchResults = document.getElementById('searchResults');
    const container = document.getElementById('faq-content-container');
    const lowerQuery = query.toLowerCase();

    // Ensure all FAQ data is loaded
    await this.loadAllFAQData();

    // Collect all matching items across all categories
    let allMatches = [];

    for (const [category, data] of Object.entries(this.faqData)) {
      if (!data || !data.sections) continue;

      const catData = this.categoryStructure[category];
      if (!catData) continue;

      data.sections.forEach(section => {
        if (!section.items) return;

        const matchingItems = [];
        section.items.forEach(item => {
          const questionMatch = item.question.toLowerCase().includes(lowerQuery);
          const answerMatch = item.answer.toLowerCase().includes(lowerQuery);
          if (questionMatch || answerMatch) {
            matchingItems.push(item);
          }
        });

        if (matchingItems.length > 0) {
          allMatches.push({
            category: category,
            categoryLabel: catData.label,
            sectionId: section.id,
            sectionTitle: section.title || '',
            items: matchingItems
          });
        }
      });
    }

    // Display result count
    const totalMatches = allMatches.reduce((sum, match) => sum + match.items.length, 0);

    searchResults.className = 'absolute pt-2 text-size-12';
    if (totalMatches === 0) {
      searchResults.textContent = `No results found for "${query}"`;
      container.innerHTML = '<p class="text-grey italic py-8">No matching FAQs found</p>';
    } else {
      searchResults.textContent = `Found ${totalMatches} result${totalMatches > 1 ? 's' : ''}`;

      // Generate HTML for all matching items
      this.displaySearchResults(allMatches, lowerQuery);
    }
  }

  /**
   * Display search results for all matching items
   */
  displaySearchResults(matches, query) {
    const container = document.getElementById('faq-content-container');
    if (!container) return;

    let html = '<div class="search-results-container">';

    // Group by category
    const categoriesMap = new Map();
    matches.forEach(match => {
      if (!categoriesMap.has(match.category)) {
        categoriesMap.set(match.category, []);
      }
      categoriesMap.get(match.category).push(match);
    });

    // Generate HTML for each category
    categoriesMap.forEach((sections, category) => {
      const catData = this.categoryStructure[category];

      html += `<div class="faq-section mb-12" data-category="${category}">`;
      html += `<h3 class="${this.titleClass} mb-6 pb-3 border-b">${catData.label}</h3>`;

      sections.forEach(section => {
        html += `<div class="faq-subsection mb-8" id="${section.sectionId}">`;
        if (section.sectionTitle) {
          html += `<h4 class="text-size-16 sm:text-size-18 font-medium mb-4">${section.sectionTitle}</h4>`;
        }
        html += '<div class="faq-items">';

        section.items.forEach(item => {
          // Highlight question
          const highlightedQuestion = this.highlightText(item.question, query);

          // Highlight answer
          const highlightedAnswer = this.highlightHTMLContent(this.parseAnswer(item.answer), query);

          html += `
            <div class="faq-item border-b active" data-original-question="${this.escapeHtml(item.question)}" data-original-answer="${this.escapeHtml(item.answer)}">
              <button class="faq-question ${this.descClass} w-full text-left py-4 flex items-start justify-between gap-4">
                <span class="flex-1">${highlightedQuestion}</span>
                <svg class="w-4 h-4 flex-shrink-0 transform rotate-180 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
              <div class="faq-answer ${this.descClass} text-grey font-light pb-4 pl-0">
                ${highlightedAnswer}
              </div>
            </div>
          `;
        });

        html += '</div></div>';
      });

      html += '</div>';
    });

    html += '</div>';

    container.innerHTML = html;

    // Attach FAQ item listeners
    this.attachFAQListeners(container);
  }

  /**
   * Filter FAQ items in current view
   */
  filterFAQItems(query) {
    const container = document.getElementById('faq-content-container');
    if (!container) return;

    const faqSections = container.querySelectorAll('.faq-subsection');
    const faqItems = container.querySelectorAll('.faq-item');
    let visibleCount = 0;

    // Filter FAQ items
    faqItems.forEach(item => {
      const questionEl = item.querySelector('.faq-question span');
      const answerEl = item.querySelector('.faq-answer');

      if (!questionEl || !answerEl) return;

      const questionText = questionEl.textContent.toLowerCase();
      const answerText = answerEl.textContent.toLowerCase();

      const matches = questionText.includes(query) || answerText.includes(query);

      if (matches) {
        item.style.display = '';
        visibleCount++;

        // Store original HTML if not already stored
        if (!questionEl.dataset.originalText) {
          questionEl.dataset.originalText = questionEl.textContent;
        }
        if (!answerEl.dataset.originalHtml) {
          answerEl.dataset.originalHtml = answerEl.innerHTML;
        }

        // Highlight matches in question
        const originalQuestion = questionEl.dataset.originalText;
        questionEl.innerHTML = this.highlightText(originalQuestion, query);

        // Highlight matches in answer (preserve HTML structure)
        const originalAnswerHtml = answerEl.dataset.originalHtml;
        answerEl.innerHTML = this.highlightHTMLContent(originalAnswerHtml, query);

        // Auto-expand all matching answers
        item.classList.add('active'); // Add active class for CSS max-height
        answerEl.classList.remove('hidden');
        const svg = item.querySelector('.faq-question svg');
        if (svg) svg.style.transform = 'rotate(180deg)';
      } else {
        item.style.display = 'none';
      }
    });

    // Show/hide subsections based on visible items
    faqSections.forEach(section => {
      const sectionItems = section.querySelectorAll('.faq-item');
      const hasVisibleItems = Array.from(sectionItems).some(item => item.style.display !== 'none');
      section.style.display = hasVisibleItems ? '' : 'none';
    });
  }

  /**
   * Clear search input and results
   */
  clearSearchInput() {
    const searchInput = document.getElementById('faqSearch');
    const searchResults = document.getElementById('searchResults');

    if (searchInput) {
      searchInput.value = '';
    }

    if (searchResults) {
      searchResults.textContent = '';
    }
  }

  /**
   * Clear search filter and restore original category content
   */
  async clearSearchFilter() {
    // Clear search input
    this.clearSearchInput();

    // Reload the current category content
    await this.loadCategoryContent(this.currentCategory);
  }

  /**
   * Load all FAQ data for comprehensive search
   */
  async loadAllFAQData() {
    const categories = Object.keys(this.categoryStructure);

    for (const category of categories) {
      if (!this.faqData[category]) {
        this.faqData[category] = await this.loadJSON(category, 'faq');
      }
    }
  }

  /**
   * Highlight text with search query
   */
  highlightText(text, query) {
    const regex = new RegExp(`(${query})`, 'gi');
    return text.replace(regex, '<mark class="bg-yellow-200">$1</mark>');
  }

  /**
   * Highlight text in HTML content while preserving HTML structure
   */
  highlightHTMLContent(html, query) {
    // Create a temporary container
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Walk through all text nodes and highlight matches
    this.highlightTextNodes(tempDiv, query);

    return tempDiv.innerHTML;
  }

  /**
   * Recursively highlight text in text nodes
   */
  highlightTextNodes(node, query) {
    if (node.nodeType === Node.TEXT_NODE) {
      // This is a text node
      const text = node.textContent;
      const lowerText = text.toLowerCase();
      const lowerQuery = query.toLowerCase();

      if (lowerText.includes(lowerQuery)) {
        // Create a temporary element to hold the highlighted HTML
        const span = document.createElement('span');
        span.innerHTML = this.highlightText(text, query);

        // Replace the text node with the highlighted content
        const parent = node.parentNode;
        while (span.firstChild) {
          parent.insertBefore(span.firstChild, node);
        }
        parent.removeChild(node);
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // This is an element node, recurse through its children
      // Convert to array to avoid issues when modifying the DOM
      Array.from(node.childNodes).forEach(child => {
        this.highlightTextNodes(child, query);
      });
    }
  }

  /**
   * Initialize sticky navigation
   */
  initStickyNav() {
    // Only initialize on desktop
    if (window.innerWidth >= 768) {
      const stickyElement = document.getElementById('sticky_nav_help');
      const container = document.querySelector('.main-content-container');

      if (stickyElement && container && typeof hcSticky !== 'undefined') {
        try {
          const stickyNav = new hcSticky('#sticky_nav_help', {
            stickTo: '.main-content-container',
            top: 20,
            bottomEnd: 100,
            responsive: {
              768: {
                disable: true
              }
            }
          });
        } catch (error) {
          // Sticky nav initialization failed
        }
      }
    }
  }

  /**
   * Initialize tabs
   */
  initTabs() {
    const tabButtons = document.querySelectorAll('.tab-btn');
    const tabPanels = document.querySelectorAll('.tab-panel');
    const searchInput = document.getElementById('faqSearch');
    const searchResults = document.getElementById('searchResults');

    tabButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const targetTab = btn.dataset.tab;

        // Update buttons
        tabButtons.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        // Update panels
        tabPanels.forEach(panel => {
          if (panel.id === targetTab + '-tab') {
            panel.classList.add('active');
          } else {
            panel.classList.remove('active');
          }
        });

        // Clear search input and results when switching tabs
        if (searchInput) {
          searchInput.value = '';
        }
        if (searchResults) {
          searchResults.textContent = '';
        }

        // Update placeholder based on active tab
        if (searchInput) {
          if (targetTab === 'tutorials') {
            searchInput.placeholder = 'Enter a keyword to find tutorial videos';
          } else {
            searchInput.placeholder = 'Enter a keyword to find your answer';
          }
        }

        // Restore FAQs navigation active state when switching to FAQs tab
        if (targetTab === 'faqs') {
          this.restoreFAQNavActiveState();
          // Clear any tutorial search
          if (window.clearTutorialSearch) {
            window.clearTutorialSearch();
          }
        }

        // Clear FAQ search when switching to tutorials
        if (targetTab === 'tutorials') {
          // Trigger clearSearchFilter to reload FAQ content without search
          this.clearSearchFilter();
        }
      });
    });
  }

  /**
   * Restore FAQs navigation active state
   */
  restoreFAQNavActiveState() {
    // Update mobile tabs
    const mobileTabs = document.querySelectorAll('.mobile-category-tab');
    let activeTab = null;
    mobileTabs.forEach(tab => {
      if (tab.dataset.category === this.currentCategory) {
        tab.classList.add('active', 'text-black', 'font-medium');
        tab.classList.remove('text-grey');
        activeTab = tab;
      } else {
        tab.classList.remove('active', 'text-black', 'font-medium');
        tab.classList.add('text-grey');
      }
    });

    // Scroll active tab into view
    if (activeTab) {
      this.scrollTabIntoView(activeTab);
    }

    // Update desktop navigation for main categories
    const categoryBtns = document.querySelectorAll('.nav-category-btn');
    categoryBtns.forEach(btn => {
      if (btn.dataset.category === this.currentCategory) {
        btn.classList.add('font-medium');
      } else {
        btn.classList.remove('font-medium');
      }
    });

    // Update desktop navigation for category links
    const categoryLinks = document.querySelectorAll('.nav-category-link');
    categoryLinks.forEach(link => {
      if (link.dataset.category === this.currentCategory) {
        link.classList.add('active', 'font-medium');
      } else {
        link.classList.remove('active', 'font-medium');
      }
    });
  }

  /**
   * Initialize
   */
  async init() {
    // Initialize tabs
    this.initTabs();

    // Generate navigation
    this.generateNavigation();

    // Initialize search
    this.initSearch();

    // Load initial category
    await this.loadCategoryContent(this.currentCategory);

    // Remove initial loading indicator
    const initialLoading = document.querySelector('.faq-initial-loading');
    if (initialLoading) {
      initialLoading.remove();
    }

    // Initialize sticky navigation
    this.initStickyNav();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.helpCenterFAQLoader = new HelpCenterFAQLoader();
    window.helpCenterFAQLoader.init();
  });
} else {
  window.helpCenterFAQLoader = new HelpCenterFAQLoader();
  window.helpCenterFAQLoader.init();
}
